"use client";

import {
  ProductCreateDialog,
  ProductEditDialog,
  ProductsFilters,
  ProductsHeader,
  ProductsTable,
  ProductViewDialog,
} from "@/components/products";
import { Skeleton } from "@/components/ui/skeleton";
import { Product } from "@/core/entities/product";
import { useDeleteProduct } from "@/hooks/repository-hooks/product/use-product";
import { useProductsStore } from "@/stores/products";
import { exportProductsToCsv } from "@/utils/csv-export";
import { useMemo, useState } from "react";

export default function ProductsPage() {
  const { products, isLoading } = useProductsStore();
  const deleteProductMutation = useDeleteProduct();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredProducts = useMemo(() => {
    const fetchedProducts = products || [];

    return fetchedProducts.filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesStatus = (() => {
        if (statusFilter === "all") return true;

        const productStatus = product.inactive ? "inactive" : "active";

        return statusFilter === productStatus;
      })();

      return matchesSearch && matchesStatus;
    });
  }, [products, searchTerm, statusFilter]);

  const handleView = (product: Product) => {
    setSelectedProduct(product);
    setViewDialogOpen(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setEditDialogOpen(true);
  };

  const handleDelete = (productId: string) => {
    deleteProductMutation.mutate({ id: productId });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48 bg-blue-200" />
          <Skeleton className="h-10 w-32 bg-blue-200" />
        </div>

        <div className="flex gap-4">
          <Skeleton className="h-10 w-64 bg-blue-200" />
          <Skeleton className="h-10 w-32 bg-blue-200" />
        </div>

        <div className="space-y-4">
          <Skeleton className="h-12 w-full bg-blue-200" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={"sk" + i} className="h-16 w-full bg-blue-200" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <ProductsHeader
        onCreateProduct={() => setCreateDialogOpen(true)}
        onExportCsv={() => exportProductsToCsv(filteredProducts)}
        productsCount={filteredProducts.length}
      />

      <ProductsFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />

      <ProductsTable
        products={filteredProducts}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <ProductViewDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        product={selectedProduct}
      />

      <ProductEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        product={selectedProduct}
      />

      <ProductCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}
